/**
 * Page-context types shared between Apex pages and the ApexProvider.
 *
 * The provider owns the live context value; pages call `setPageContext`
 * (returned from `useApexContext()`) to push their currently-open entity,
 * filter state, and recent edits into the body sent to /aiHandler.
 *
 * Backend (slice 8) interpolates these fields into the system prompt so
 * Apex knows what the user is looking at without round-trips.
 */

export type EntityType = "project" | "intake" | "vendor";

export interface OpenEntity {
  type: EntityType;
  id: string;
  name: string;
  status?: string;
}

export interface RecentEdit {
  action: string;
  collection: string;
  docId: string;
  ts: string;
}

export interface PageContext {
  /** Pathname, e.g. "/projects". Always present. */
  route: string;
  /** Friendly name, e.g. "Projects". Always present. */
  routeName: string;
  /** Currently-focused row/modal target, when any. */
  openEntity?: OpenEntity;
  /** Active filter state on the page (free-form per page). */
  filters?: Record<string, unknown>;
  /** Last 5 audit entries for the current user; populated when the drawer opens. */
  recentEdits?: RecentEdit[];
  /** Convenience for the prompt: count of unread notifications. */
  unreadNotificationCount?: number;
}

export const ROUTE_NAMES: Record<string, string> = {
  "/": "Dashboard",
  "/projects": "Projects",
  "/intakes": "Intakes",
  "/board": "Board",
  "/vendors": "Vendors",
  "/settings": "Settings",
  "/agency": "Agency Intake (public)",
  "/vendor": "Vendor Intake (public)",
  "/signin": "Sign In",
  "/request-access": "Request Access",
};

export function routeName(pathname: string): string {
  if (ROUTE_NAMES[pathname]) return ROUTE_NAMES[pathname];
  // Fallback: derive from last segment.
  const seg = pathname.split("/").filter(Boolean).pop() || "Dashboard";
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}
